import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import {
    AIConfigNodeType,
    ExecutionLog,
    MomentType,
    RuleEngineConfig,
    RuleVersion,
    TestRule,
} from "@convin/modules/settings/modules/auditManager/components/types/bert";
import { PaginationType } from "@convin/type/Common";
import { EntityDataTypes } from "@convin/type/Entity";
import { BertNodeKeys } from "@convin/modules/settings/modules/auditManager/components/bertRuleEngine/constants";
import { newGrpcPayloadConstructor } from "@convin/utils/helper/common.helper";
import { SingleResponseBlockType } from "@convin/modules/settings/modules/auditManager/components/bertRuleEngine/context/NodeStateContext";
import { Question } from "@convin/type/Audit";

export const auditAiConfig = createApi({
    reducerPath: "auditAiConfig",
    keepUnusedDataFor: 0,
    baseQuery: axiosBaseQuery({
        transformResponse: (response) => response,
    }),
    endpoints: (builder) => ({
        getAIAuditConfig: builder.query<
            RuleEngineConfig,
            { question_id: number }
        >({
            query: (payload) => ({
                url: "audit/rule/edit/",
                method: "GET",
                params: payload,
            }),
        }),
        createAIAuditConfig: builder.mutation<
            RuleEngineConfig,
            Omit<
                RuleEngineConfig,
                "id" | "config" | "created_at" | "updated_at" | "published_at"
            >
        >({
            query: (payload) => ({
                url: "audit/rule/",
                method: "POST",
                body: payload,
            }),
        }),
        runRule: builder.mutation<
            void,
            { question_id: number; meeting_id: number }
        >({
            query: (payload) => ({
                url: "audit/rule/execute/",
                method: "POST",
                body: payload,
            }),
        }),
        updateAIAuditConfig: builder.mutation<
            RuleEngineConfig,
            Omit<RuleEngineConfig, "config" | "updated_at">
        >({
            query: (payload) => ({
                url: "audit/rule/edit/",
                method: "PUT",
                body: payload,
            }),
            async onQueryStarted(
                { question_id },
                { queryFulfilled, dispatch }
            ) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        auditAiConfig.util.updateQueryData(
                            "getAIAuditConfig",
                            {
                                question_id,
                            },
                            (draft) => {
                                return { ...draft, ...data };
                            }
                        )
                    );
                } catch {}
            },
        }),
        saveAIAuditConfig: builder.mutation<
            RuleEngineConfig,
            { validate: boolean; question_id: number } & Omit<
                RuleEngineConfig,
                "config" | "internal_config" | "published_at"
            >
        >({
            query: (payload) => ({
                url: "audit/rule/edit/",
                method: "PUT",
                body: payload,
            }),
            async onQueryStarted(
                { question_id },
                { queryFulfilled, dispatch }
            ) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        auditAiConfig.util.updateQueryData(
                            "getAIAuditConfig",
                            {
                                question_id,
                            },
                            (draft) => {
                                return { ...draft, ...data };
                            }
                        )
                    );
                } catch {}
            },
        }),
        deleteAIAuditConfig: builder.mutation<void, { rule_id: string }>({
            query: (payload) => ({
                url: "audit/rule/edit/",
                method: "DELETE",
                body: payload,
            }),
        }),
        createMoment: builder.mutation<MomentType, Omit<MomentType, "id">>({
            query: (payload) => ({
                url: "audit/moments/",
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        auditAiConfig.util.updateQueryData(
                            "getMoments",
                            undefined,
                            (draft) => ({
                                moments: [...draft.moments, data],
                            })
                        )
                    );
                } catch {}
            },
        }),
        updateMoment: builder.mutation<MomentType, MomentType>({
            query: (payload) => ({
                url: "audit/moments/edit/",
                method: "PUT",
                body: payload,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        auditAiConfig.util.updateQueryData(
                            "getMoments",
                            undefined,
                            (draft) => ({
                                moments: draft.moments.map((e) =>
                                    e.id === data.id ? data : e
                                ),
                            })
                        )
                    );
                } catch {}
            },
        }),
        getMoments: builder.query<{ moments: MomentType[] }, void>({
            query: () => "audit/moments/",
        }),
        getAttributeCategories: builder.query<
            Array<{ id: number; name: string }>,
            void
        >({
            query: () => "meeting/metadata/types/",
        }),
        getDataTypeOperators: builder.query<
            {
                operators: Record<string, string[]>;
                data_types: Array<EntityDataTypes>;
            },
            void
        >({
            query: () => "meeting/metadata/operators/",
        }),
        getAttributeCategoryKeys: builder.query<
            PaginationType<{
                id: number;
                name: string;
                data_type: EntityDataTypes;
                metadata_type: number;
            }>,
            {
                next?: string;
                query?: string;
                metadata_type?: number;
                data_type?: string;
                id?: number;
            }
        >({
            query: ({ next, ...payload }) => ({
                url: next || "/meeting/metadata/keys/",
                method: "POST",
                body: payload,
            }),
        }),
        getExecutionLogs: builder.query<
            ExecutionLog,
            { id: number; question_id: number }
        >({
            query: ({ id, ...params }) => ({
                url: `/audit/rule/execution_log/${id}/`,
                method: "GET",
                params,
            }),
        }),
        getExistingBlocksData: builder.query<
            {
                nodes: Array<
                    AIConfigNodeType & {
                        block_name: string;
                        data_type: NonNullable<SingleResponseBlockType>;
                    }
                >;
                last_evaluated_key: string | null;
            },
            {
                last_evaluated_key?: string | null;
                block_name?: string;
                type?:
                    | BertNodeKeys.singleBlockNode
                    | BertNodeKeys.groupBlockNode;
                data_type?: NonNullable<SingleResponseBlockType>;
            }
        >({
            query: ({ last_evaluated_key, block_name, type, data_type }) => {
                const filter = newGrpcPayloadConstructor({
                    ...(block_name && {
                        block_name: { begins_with: block_name },
                    }),
                    ...(type && { type: { eq: type } }),
                    ...(data_type && { data_type: { eq: data_type } }),
                });
                return {
                    url: "/audit/get_nodes/",
                    method: "POST",
                    body: {
                        last_evaluated_key,
                        page_size: 30,
                        filter,
                    },
                };
            },
        }),
        getTestRule: builder.query<
            TestRule[],
            {
                id: string;
                question_id: number;
                template_id: number;
                meeting_links: string[];
            }
        >({
            query: (payload) => ({
                url: "/audit/rule/test/",
                method: "POST",
                body: payload,
            }),
        }),
        deleteNode: builder.mutation<void, number | string>({
            query: (id) => ({
                url: `/audit/delete_nodes/${id}/`,
                method: "DELETE",
            }),
        }),
        getPreviousVersion: builder.query<
            { rule_versions: Array<RuleVersion> },
            {
                question_id: number;
                own_rule_versions: boolean;
                version?: number;
            }
        >({
            query: (payload) => ({
                url: "/audit/rule/versions/",
                method: "POST",
                body: payload,
            }),
        }),
        getRuleBasedOnVersion: builder.query<
            RuleVersion & {
                rule_draft_config: RuleEngineConfig["draft_config"];
                rule_config: RuleEngineConfig["config"];
                question_config: Question;
            },
            {
                question_id: number;
                version: number;
            }
        >({
            query: (payload) => ({
                url: "/audit/rule/versions/",
                method: "POST",
                body: payload,
            }),
        }),
        restoreRuleVersion: builder.mutation<
            void,
            { question_id: number; version: number }
        >({
            query: (payload) => ({
                url: "/audit/rule/version/restore/",
                method: "POST",
                body: payload,
            }),
        }),
        getAttributeValues: builder.query<
            PaginationType<string>,
            { metadata_type: number; key_id: number; query: string }
        >({
            query: (payload) => ({
                url: "meeting/metadata/values/",
                method: "POST",
                body: payload,
            }),
        }),
    }),
});

export const {
    useCreateMomentMutation,
    useUpdateMomentMutation,
    useGetMomentsQuery,
    useGetAIAuditConfigQuery,
    useCreateAIAuditConfigMutation,
    useUpdateAIAuditConfigMutation,
    useDeleteAIAuditConfigMutation,
    useSaveAIAuditConfigMutation,
    useRunRuleMutation,
    useLazyGetAIAuditConfigQuery,
    useGetAttributeCategoriesQuery,
    useGetAttributeCategoryKeysQuery,
    useLazyGetAttributeCategoryKeysQuery,
    useGetDataTypeOperatorsQuery,
    useGetExistingBlocksDataQuery,
    useLazyGetExistingBlocksDataQuery,
    useGetExecutionLogsQuery,
    useLazyGetTestRuleQuery,
    useDeleteNodeMutation,
    useGetPreviousVersionQuery,
    useGetRuleBasedOnVersionQuery,
    useRestoreRuleVersionMutation,
    useLazyGetAttributeValuesQuery,
} = auditAiConfig;
